import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { rrhhApi, vacacionesApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DashboardSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import {
  Clock, Users, Briefcase, TrendingUp,
  BarChart3, HardHat, Building2, Wrench, Plane, ShieldCheck
} from 'lucide-react';

const fmtF = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

const KpiCard = ({ label, value, subtitle, icon: Icon, color = 'primary' }) => {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    red: 'bg-red-500/10 text-red-600 dark:text-red-400',
    blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  };
  return (
    <Card className="transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold tabular-nums">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-xl ${colorMap[color]}`}>
            <Icon className="h-5 w-5" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function RRHHDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState('');
  const [vac, setVac] = useState(null);

  useEffect(() => {
    vacacionesApi.resumenDashboard().then((r) => setVac(r.data)).catch(() => {});
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const params = mes ? { mes } : {};
      const res = await rrhhApi.getDashboard(params);
      setData(res.data);
    } catch (err) {
      toast({ title: 'Error al cargar dashboard RRHH', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading && !data) return <DashboardSkeleton cards={6} />;

  const d = data || {};

  return (
    <div className="space-y-6 page-enter" data-testid="rrhh-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard RRHH</h1>
          <p className="text-sm text-muted-foreground">
            Periodo: {d.mes || 'Mes actual'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            className="h-9 rounded-xl border bg-background px-3 text-sm"
            onChange={(e) => setMes(e.target.value)}
            data-testid="rrhh-month-selector"
          />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          label="Total HH"
          value={d.total_horas || 0}
          subtitle={`${d.total_horas_normales || 0} HN + ${d.total_horas_extra || 0} HEX`}
          icon={Clock}
          color="primary"
        />
        <KpiCard
          label="HH Productivas"
          value={d.hh_productivas || 0}
          subtitle="Imputadas a OTs"
          icon={Briefcase}
          color="green"
        />
        <KpiCard
          label="HH No Productivas"
          value={d.hh_no_productivas || 0}
          subtitle="Gastos generales"
          icon={Building2}
          color="amber"
        />
        <KpiCard
          label="Trabajadores"
          value={`${d.trabajadores_mes || 0} / ${d.trabajadores_activos || 0}`}
          subtitle="Mes / Activos"
          icon={Users}
          color="blue"
        />
        <KpiCard
          label="OTs con HH"
          value={d.ots_mes || 0}
          subtitle="Ordenes de trabajo"
          icon={Wrench}
          color="primary"
        />
        <KpiCard
          label="Registros"
          value={d.registros || 0}
          subtitle="Informes cargados"
          icon={BarChart3}
          color="green"
        />
      </div>

      {/* Vacaciones — quién está hoy de vacaciones y solicitudes por aprobar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="rrhh-vacaciones-widget">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Plane className="h-4 w-4" strokeWidth={1.5} /> De vacaciones hoy
              </CardTitle>
              <Link to="/rrhh/vacaciones" className="text-xs text-blue-600 hover:underline" data-testid="link-vacaciones">Ir al módulo</Link>
            </div>
          </CardHeader>
          <CardContent>
            {(vac?.hoy || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nadie de vacaciones hoy</p>
            ) : (
              <div className="space-y-2">
                {(vac.hoy).map((v) => (
                  <div key={v.id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0 text-sm" data-testid={`dash-hoy-${v.id}`}>
                    <span className="font-medium truncate">{v.trabajador_nombre}</span>
                    <span className="text-xs text-muted-foreground">hasta {fmtF(v.fecha_fin)} · reint. {fmtF(v.fecha_reintegro)}</span>
                  </div>
                ))}
              </div>
            )}
            {(vac?.proximas || []).length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Próximas (30 días)</p>
                {(vac.proximas).slice(0, 5).map((v) => (
                  <div key={v.id} className="flex items-center justify-between text-xs py-0.5">
                    <span className="truncate">{v.trabajador_nombre}</span>
                    <span className="text-muted-foreground">{fmtF(v.fecha_inicio)} → {fmtF(v.fecha_fin)} ({v.dias_habiles}d)</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> Vacaciones por aprobar{vac?.pendientes?.total ? ` (${vac.pendientes.total})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(vac?.pendientes?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Sin solicitudes pendientes</p>
            ) : (
              <div className="space-y-1">
                {(vac.pendientes.items).map((v) => (
                  <Link to="/rrhh/vacaciones" key={v.id} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0 text-sm hover:bg-muted/40 rounded px-1 -mx-1" data-testid={`dash-pendiente-${v.id}`}>
                    <span className="font-medium truncate">{v.trabajador_nombre}</span>
                    <span className="text-xs text-muted-foreground">{fmtF(v.fecha_inicio)} → {fmtF(v.fecha_fin)} · {v.mi_nivel === 1 ? 'N1 Jefatura' : 'N2 RRHH'}</span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main content: 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HH por Area */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <HardHat className="h-4 w-4" strokeWidth={1.5} />
              HH por Area de Origen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(d.por_area || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Sin datos para este periodo</p>
            ) : (
              <div className="space-y-3">
                {(d.por_area || []).map((a, i) => {
                  const maxHoras = Math.max(...(d.por_area || []).map(x => x.total_horas), 1);
                  const pct = (a.total_horas / maxHoras) * 100;
                  return (
                    <div key={a.area} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{a.area}</span>
                        <span className="tabular-nums text-muted-foreground">{a.total_horas} hrs ({a.registros} reg.)</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Trabajadores */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
              Top Trabajadores del Mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(d.top_trabajadores || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Sin datos para este periodo</p>
            ) : (
              <div className="space-y-2">
                {(d.top_trabajadores || []).map((t, i) => (
                  <div key={t.numero || `t-${i}`} className="flex items-center gap-3 py-1.5 border-b border-border/50 last:border-0">
                    <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.nombre || `#${t.numero}`}</p>
                      <p className="text-xs text-muted-foreground">{t.dias} dias</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold tabular-nums">{t.total_horas} hrs</p>
                      {t.horas_extra > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">+{t.horas_extra} HEX</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top OTs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Wrench className="h-4 w-4" strokeWidth={1.5} />
            Top OTs por Horas Consumidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(d.top_ots || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Sin datos para este periodo</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {(d.top_ots || []).map((ot, i) => (
                <div key={ot.ot_numero || `ot-${i}`} className="rounded-xl border bg-card p-3 text-center transition-all hover:shadow-sm">
                  <p className="text-lg font-bold tabular-nums text-primary">OT {ot.ot_numero}</p>
                  <p className="text-sm font-semibold tabular-nums">{ot.total_horas} hrs</p>
                  <p className="text-xs text-muted-foreground">{ot.cant_trabajadores} trabajador{ot.cant_trabajadores !== 1 ? 'es' : ''}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
